import { CheckIcon } from "@heroicons/react/20/solid";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const tiers = [
  {
    name: "Launch",
    id: "tier-launch",
    price: { monthly: "$95" },
    description: "Perfect for new and growing martial arts schools",
    studentLimit: "Up to 100 students",
    featured: false,
  },
  {
    name: "Scale",
    id: "tier-scale",
    price: { monthly: "$125" },
    description: "Ideal for expanding schools",
    studentLimit: "Up to 200 students",
    featured: true,
  },
  {
    name: "Expand",
    id: "tier-expand",
    price: { monthly: "$165" },
    description: "For established schools with growing student bases",
    studentLimit: "Up to 300 students",
    featured: false,
  },
  {
    name: "Grow",
    id: "tier-grow",
    price: { monthly: "$195" },
    description: "For large schools with established operations",
    studentLimit: "Up to 500 students",
    featured: false,
  },
  {
    name: "Master",
    id: "tier-master",
    price: { monthly: "$245" },
    description: "For premier schools with unlimited growth potential",
    studentLimit: "501+ students",
    featured: false,
  },
];

const featureCategories = [
  {
    title: "Studio Management",
    features: [
      "Real-time studio analytics & performance insights",
      "Easy instructor management for classes",
      "Smart class & event management",
      "One-click attendance tracking",
      "Student self-check-in kiosk (no more lines)",
      "Integrated simple point-of-sale system for seamless transactions",
    ]
  },
  {
    title: "Student Experience",
    features: [
      "AI-powered student retention & drop-off prevention",
      "Automated belt promotion tracking & notifications",
      "Self-service payment updates (reduces front desk calls)",
      "Smart email & SMS communications",
      "Digital waiver management & electronic signatures",
      "Proactive payment reminders before cards expire",
    ]
  },
  {
    title: "Business Growth",
    features: [
      "Complete billing & payment processing",
      "Smart membership management & auto-renewals",
      "AI lead scoring to convert more prospects",
      "Event registration with integrated payments",
      "Custom website forms & lead capture",
      "Staff roles & permission management",
      "Multi-user access for your team",
      "Automated lead nurturing with instant appointment booking",
    ]
  }
];

export default function Pricing() {
  return (
    <>
      <Helmet>
        <title>Martial Arts CRM Pricing | DojoTrack</title>
        <meta
          name="description"
          content="Choose the right DojoTrack plan for your martial arts studio. Launch ($95), Scale ($125), Expand ($165), Grow ($195), and Master ($245) plans. All plans include full features with 14-day free trial."
        />
        <meta
          name="keywords"
          content="dojo software pricing, martial arts management pricing, dojo track pricing, martial arts studio software cost, dojo management subscription"
        />
        <meta name="author" content="DojoTrack LLC" />
        <meta
          property="og:title"
          content="Martial Arts CRM Pricing | DojoTrack"
        />
        <meta
          property="og:description"
          content="Choose the right DojoTrack plan for your martial arts studio. Launch ($95), Scale ($125), Expand ($165), Grow ($195), and Master ($245) plans. All plans include full features with 14-day free trial."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dojotrack.com/pricing" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Martial Arts CRM Pricing | DojoTrack"
        />
        <meta
          name="twitter:description"
          content="Choose the right DojoTrack plan for your martial arts studio. Launch ($95), Scale ($125), Expand ($165), Grow ($195), and Master ($245) plans. All plans include full features with 14-day free trial."
        />
        <link rel="canonical" href="https://dojotrack.com/pricing" />
      </Helmet>
      <div className="bg-white dark:bg-gray-900 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">
              Pricing
            </h2>
            <p className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
              <span className="text-gray-900 dark:text-white">Choose the right plan for your </span>
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">studio</span>
            </p>
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600 dark:text-gray-400">
            All plans include our complete feature set. Choose the plan that
            best fits your student count.
          </p>
          
          {/* Value Proposition */}
          <div className="mx-auto mt-12 max-w-3xl text-center">
            <div className="rounded-2xl bg-gradient-to-br from-indigo-50/80 to-purple-50/80 dark:from-indigo-900/30 dark:to-purple-900/30 p-8 border border-indigo-200 dark:border-indigo-700 shadow-lg">
              <h3 className="text-xl font-bold mb-3">
                <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Incredible Value for Your Investment</span>
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                For less than the cost of 1-2 student memberships, you get access to our complete suite of professional-grade tools that will transform how you manage your studio, boost student retention, and accelerate your business growth.
              </p>
            </div>
          </div>
          
          {/* Features Section */}
          <div className="mx-auto mt-16 max-w-6xl">
            <h3 className="text-2xl font-bold text-center mb-4">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Everything included in all plans</span>
            </h3>
            <p className="text-center text-gray-600 dark:text-gray-400 mb-12">
              Powerful features that grow with your studio
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {featureCategories.map((category) => (
                <div key={category.title} className="space-y-6">
                  <div className="text-center">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                      {category.title}
                    </h4>
                  </div>
                  <div className="space-y-4">
                    {category.features.map((feature) => (
                      <div key={feature} className="group relative bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-300 transform hover:-translate-y-1">
                        <div className="flex items-center gap-x-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" />
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed">
                              {feature}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/features"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                View all features
              </Link>
            </div>
          </div>
          <div className="isolate mx-auto mt-20 grid max-w-md grid-cols-1 gap-y-8 sm:mt-24 lg:mx-0 lg:max-w-none lg:grid-cols-3 xl:grid-cols-5">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className={`flex flex-col justify-between rounded-3xl bg-white dark:bg-gray-800 p-6 ring-1 ring-gray-200 dark:ring-gray-700 xl:p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                  tier.featured
                    ? "ring-2 ring-indigo-600 dark:ring-indigo-500 shadow-lg"
                    : "hover:ring-indigo-300 dark:hover:ring-indigo-600"
                }`}
              >
                <div>
                  <div className="flex items-center justify-between gap-x-4">
                    <h3
                      className={`text-lg font-semibold leading-8 ${
                        tier.featured
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-gray-900 dark:text-white"
                      }`}
                    >
                      {tier.name}
                    </h3>
                    {tier.featured && (
                      <p className="rounded-full bg-indigo-600/10 dark:bg-indigo-400/10 px-2.5 py-1 text-xs font-semibold leading-5 text-indigo-600 dark:text-indigo-400">
                        Most popular
                      </p>
                    )}
                  </div>
                  <p className="mt-4 text-xs leading-5 text-gray-600 dark:text-gray-400">
                    {tier.description}
                  </p>
                  <p className="mt-6 flex items-baseline gap-x-1">
                    <span className={`text-3xl font-bold tracking-tight ${
                      tier.featured 
                        ? "bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
                        : "text-gray-900 dark:text-white"
                    }`}>
                      {tier.price.monthly}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-gray-600 dark:text-gray-400">
                      /month
                    </span>
                  </p>
                  <div className="mt-6">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      {tier.studentLimit}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      All plans include our complete feature set
                    </p>
                  </div>
                </div>
                <a
                  href="/signup"
                  className={`mt-8 block rounded-md px-4 py-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-all duration-300 ${
                    tier.featured
                      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:from-indigo-500 hover:to-purple-500 focus-visible:outline-indigo-600 transform hover:scale-105 hover:shadow-xl"
                      : "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-800/50 focus-visible:outline-indigo-600 hover:shadow-md"
                  }`}
                >
                  Get started today
                </a>
              </div>
            ))}
          </div>

          {/* SMS Add-on Pricing */}
          <div className="mx-auto mt-20 max-w-4xl">
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/30 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  SMS & Communication Add-ons
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Optional — Choose the SMS solution that works best for you
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Option 1: Own Twilio */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
                        <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Use Your Own Twilio
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Connect your existing Twilio account at no additional cost to DojoTrack.
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        You pay Twilio directly for SMS usage
                      </p>
                    </div>
                  </div>
                </div>

                {/* Option 2: Rent from DojoTrack */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-indigo-200 dark:border-indigo-700 relative">
                  <div className="absolute -top-3 right-4">
                    <span className="inline-flex items-center rounded-full bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                      Hassle-free
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-600">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Rent a DojoTrack Number
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        We handle everything — just send messages through DojoTrack.
                      </p>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          $2/month <span className="font-normal text-gray-500">phone rental</span>
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          $0.017 <span className="font-normal text-gray-500">per text message</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial Quote */}
          {/* <div className="mx-auto mt-16 max-w-3xl text-center">
            <div className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 p-8 border border-indigo-200 dark:border-indigo-700">
              <blockquote className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                "DojoTrack has transformed how we manage our martial arts studio. The student management and attendance tracking features alone have saved us hours every week. It's like having an extra staff member!"
              </blockquote>
              <div className="flex items-center justify-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">SM</span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Sarah Martinez
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Owner, Phoenix Martial Arts Academy
                  </p>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </>
  );
}
